import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import * as yup from "yup";

import {
  useCreateCompanyMutation,
  useGetCreateCompanyIndustryTypesQuery,
} from "@/services/(my-space)/userApi";
import { Select } from "@/components/Select";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/Textfield";
import { PhoneInput } from "@/components/PhoneInput";
import { companyInfoInitialState } from "@/validations/create-company/data";
import { companyInfoValidationSchema } from "@/validations/create-company/schema";
import { companiesApi } from "@/services/(my-space)/companiesApi";
import { authApi } from "@/services/authApi";
import { setAuthTokensToCookie } from "@/utils/auth";
import { cn } from "@/utils/cn";

type Fields = yup.InferType<typeof companyInfoValidationSchema>;

interface Props {
  onSuccess?: () => void;
  /**
   * Renders a Cancel button beside Submit. Omit on surfaces with nowhere to go
   * back to (the standalone onboarding page).
   */
  onCancel?: () => void;
  /**
   * Field layout. `2` pairs fields from `sm` up, which roughly halves the
   * form's height — use it in the dialog. The standalone onboarding page is a
   * narrow column, so it stays at `1`.
   */
  columns?: 1 | 2;
  /** Submit label. Defaults to "Create Company". */
  submitLabel?: string;
  className?: string;
}

export const AddCompanyForm = ({
  onSuccess,
  onCancel,
  columns = 1,
  submitLabel = "Create Company",
  className,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const dispatch = useDispatch();
  const [createCompany, createCompanyState] = useCreateCompanyMutation();
  const { data: industries, isLoading: isIndustriesLoading } =
    useGetCreateCompanyIndustryTypesQuery();

  const isTwoCol = columns === 2;

  const formik = useFormik<Fields>({
    initialValues: companyInfoInitialState,
    validationSchema: companyInfoValidationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      const id = toast.loading("Processing...");
      await createCompany(values)
        .unwrap()
        .then((res) => {
          toast.success(res.message, { id });
          const { accessToken, refreshToken, deviceId } = res.data;
          if (accessToken && deviceId)
            setAuthTokensToCookie(accessToken, refreshToken, deviceId);
          // Refresh companies list
          dispatch(
            companiesApi.util.invalidateTags([
              "getEmployeeCompanies",
              "getSuperAdminCompanies",
            ]),
          );
          // Invalidate auth profile cache so middleware gets fresh data with new company
          dispatch(authApi.util.invalidateTags(["USER_PROFILE"]));
          // After success action if any
          if (onSuccess) onSuccess();
        })
        .catch((e) =>
          toast.error(
            e?.data?.message ??
              e.data?.errors?.message ??
              t("my-space:shared.generic_error"),
            {
              id,
            },
          ),
        );
    },
  });

  return (
    <form
      key="comany-information"
      className={cn("mx-auto w-full space-y-6", className)}
      onSubmit={formik.handleSubmit}
    >
      <div className={cn("grid gap-4", isTwoCol && "sm:grid-cols-2")}>
        <TextField
          type="text"
          label={t("common:a11y.company_name")}
          name="name"
          formik={formik}
          className="h-12!"
          placeholder={t("common:placeholders.enter_your_company_name")}
          autoComplete="organization"
          // The identity field leads, full width, on its own row.
          rootClassName={cn(isTwoCol && "sm:col-span-2")}
        />
        <TextField
          type="email"
          label={t("company:employees.company_email")}
          name="email"
          formik={formik}
          className="h-12!"
          // eslint-disable-next-line i18next/no-literal-string -- locale-neutral sample email address
          placeholder="company@yourdomain.com"
          autoComplete="email"
        />
        <TextField
          type="text"
          label={t("common:a11y.portal_name")}
          name="portalName"
          formik={formik}
          className="h-12!"
          placeholder={t("common:placeholders.enter_your_portal_name")}
          autoComplete="off"
        />
        <Select
          label={t("common:onboarding.industry")}
          name="industry"
          options={industries?.data || []}
          value={formik.values.industry}
          onChange={(val) => formik.setFieldValue("industry", val)}
          error={formik.touched.industry ? formik.errors.industry : undefined}
          loading={isIndustriesLoading}
          showSearch
          searchPlaceholder={t("common:onboarding.search_industries")}
          placeholder={t("common:placeholders.select_an_industry")}
        />
        <PhoneInput
          label={t("company:employees.phone_number")}
          value={formik.values.phoneNumber}
          setFieldValue={(value) => formik.setFieldValue("phoneNumber", value)}
          setFieldError={(error) => formik.setFieldError("phoneNumber", error)}
          error={
            formik.touched.phoneNumber && formik.errors.phoneNumber
              ? formik.errors.phoneNumber
              : undefined
          }
          styles={{
            inputClassName: "border-surface-elevated",
          }}
        />
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={createCompanyState.isLoading}
          >
            {t("common:buttons.cancel")}
          </Button>
        )}
        <Button
          type="submit"
          isLoading={createCompanyState.isLoading}
          className="sm:min-w-40"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
